import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Command } from "@air/cmdk";
import * as RadixPopover from "@radix-ui/react-popover";
import classNames from "classnames";
import { memo, useCallback, useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { SearchCommandActions } from "~/components/SearchCommand/SearchCommandActions";
import { SearchCommandClearButton } from "~/components/SearchCommand/SearchCommandClearButton";
import { SearchCommandInputBase } from "~/components/SearchCommand/SearchCommandInputBase";
import { SearchCommandList } from "~/components/SearchCommand/SearchCommandList";
import { SearchCommandTrigger } from "~/components/SearchCommand/SearchCommandTrigger";
import { SmartSearchCommandList } from "~/components/SearchCommand/SmartSearchCommandList";
import { useTemporaryFeatureFlags } from "~/hooks/useTemporaryFeatureFlags";
import { usePrivateSearchContext } from "~/providers/PrivateSearchProvider";
export const SearchCommand = /*#__PURE__*/ memo((param)=>{
    let { filterBar, filtersCount, hideSuggestions = false, onBoardClick: _onBoardClick, onClear, onOpenChange, onSavedFilterSelect: _onSavedFilterSelect, onSearchChange, onSuggestionClick: _onSuggestionClick, placeholder, renderActions, renderCustomContent, search, suggestions } = param;
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState(undefined);
    const { ui2 } = useTemporaryFeatureFlags();
    const { searchType, canUseSmartSearch } = usePrivateSearchContext();
    /**
     * Update the searchValue when the search prop changes.
     */ useEffect(()=>{
        setSearchValue(search);
    }, [
        search
    ]);
    const onOpen = useCallback(()=>{
        setIsOpen(true);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(true);
    }, [
        onOpenChange
    ]);
    const onBoardClick = useCallback((board)=>{
        setIsOpen(false);
        _onBoardClick === null || _onBoardClick === void 0 ? void 0 : _onBoardClick(board);
    }, [
        _onBoardClick
    ]);
    const onSavedFilterSelect = useCallback((savedFilter)=>{
        setIsOpen(false);
        _onSavedFilterSelect === null || _onSavedFilterSelect === void 0 ? void 0 : _onSavedFilterSelect(savedFilter);
    }, [
        _onSavedFilterSelect
    ]);
    const onSuggestionClick = useCallback((suggestion)=>{
        setIsOpen(false);
        setSearchValue(suggestion);
        _onSuggestionClick === null || _onSuggestionClick === void 0 ? void 0 : _onSuggestionClick(suggestion);
    }, [
        _onSuggestionClick
    ]);
    const onValueChange = useCallback((value)=>{
        setSearchValue(value);
        onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange(value);
    }, [
        onSearchChange
    ]);
    const onClearSearchValue = useCallback(()=>{
        setSearchValue("");
        onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange("");
    }, [
        onSearchChange
    ]);
    useHotkeys("meta+p,ctrl+p", onOpen, {
        enableOnFormTags: true,
        preventDefault: true
    });
    return /*#__PURE__*/ _jsx(Command, {
        className: "w-full",
        "data-testid": "SEARCH_COMMAND",
        shouldFilter: false,
        children: /*#__PURE__*/ _jsxs(RadixPopover.Root, {
            open: isOpen,
            onOpenChange: setIsOpen,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "relative",
                    children: [
                        /*#__PURE__*/ _jsx(RadixPopover.Trigger, {
                            asChild: true,
                            children: /*#__PURE__*/ _jsx(SearchCommandTrigger, {
                                count: filtersCount,
                                onClick: onOpen,
                                placeholder: placeholder,
                                search: searchValue
                            })
                        }),
                        !!renderActions && /*#__PURE__*/ _jsx(SearchCommandActions, {
                            children: renderActions({
                                onClearSearchValue
                            })
                        }),
                        !!onClear && /*#__PURE__*/ _jsx(SearchCommandClearButton, {
                            onClick: onClear
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(RadixPopover.Portal, {
                    children: /*#__PURE__*/ _jsxs(RadixPopover.Content, {
                        className: classNames("relative left-0 top-[calc(var(--radix-popover-trigger-height)*-1)] flex max-h-[calc(var(--radix-popover-content-available-height)-32px)] w-[var(--radix-popover-trigger-width)] flex-col transition-all data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in", ui2 ? "bg-grey-transparent gap-1 rounded-8" : "overflow-hidden rounded-4 border border-grey-5 bg-transparent ring-2 transition-[box-shadow]", !ui2 && isOpen ? "ring-black/[0.04] dark:ring-white/[0.025]" : !ui2 ? "ring-transparent" : ""),
                        "data-testid": "SEARCH_COMMAND_CONTENT",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: classNames("flex w-full shrink-0 flex-col", ui2 ? "rounded-8 border border-grey-6 bg-grey-1" : "border-b border-b-grey-5 bg-grey-1"),
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "relative",
                                        children: [
                                            /*#__PURE__*/ _jsx(SearchCommandInputBase, {
                                                count: filtersCount,
                                                onValueChange: onValueChange,
                                                placeholder: placeholder,
                                                value: searchValue,
                                                canUseSmartSearch: canUseSmartSearch
                                            }),
                                            !!renderActions && /*#__PURE__*/ _jsx(SearchCommandActions, {
                                                children: renderActions({
                                                    onClearSearchValue
                                                })
                                            }),
                                            !!onClear && /*#__PURE__*/ _jsx(SearchCommandClearButton, {
                                                onClick: onClear
                                            })
                                        ]
                                    }),
                                    searchType === "keyword" && filterBar
                                ]
                            }),
                            searchType === "keyword" && /*#__PURE__*/ _jsx(SearchCommandList, {
                                isOpen: isOpen,
                                hideSuggestions: hideSuggestions,
                                searchValue: searchValue,
                                suggestions: suggestions,
                                renderCustomContent: renderCustomContent,
                                onSuggestionClick: onSuggestionClick,
                                onSavedFilterSelect: onSavedFilterSelect,
                                onBoardClick: onBoardClick
                            }),
                            searchType === "smart" && /*#__PURE__*/ _jsx(SmartSearchCommandList, {
                                onSelect: onSuggestionClick
                            })
                        ]
                    })
                })
            ]
        })
    });
});
SearchCommand.displayName = "SearchCommand";
